import type { GossipGroup } from "@/types/content";

interface GroupCardProps {
  group: GossipGroup;
  showRank?: boolean;
}

const trendMeta: Record<string, { icon: string; className: string; label: string }> = {
  up: { icon: "ri-arrow-up-line", className: "text-accent-700 bg-accent-100", label: "热度上升" },
  down: { icon: "ri-arrow-down-line", className: "text-primary-700 bg-primary-100", label: "热度回落" },
  flat: { icon: "ri-subtract-line", className: "text-secondary-900 bg-secondary-100", label: "热度持平" },
};

function Stars({ rating }: { rating: number }) {
  return (
    <span className="inline-flex items-center gap-0.5 text-secondary-500">
      {[0, 1, 2, 3, 4].map((i) => (
        <i
          key={i}
          className={`text-sm ${i < Math.round(rating) ? "ri-star-fill" : "ri-star-line text-background-400"}`}
        ></i>
      ))}
    </span>
  );
}

export default function GroupCard({ group, showRank = true }: GroupCardProps) {
  const trend = trendMeta[group.trend] ?? trendMeta.flat;

  return (
    <article
      data-product-shop
      className="group flex h-full flex-col rounded-lg border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-300"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          {showRank ? (
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-500 font-heading text-sm text-background-50">
              {group.rank}
            </span>
          ) : null}
          <div className="min-w-0">
            <h3 className="truncate font-heading text-base md:text-lg text-foreground-950">{group.name}</h3>
            <p className="mt-0.5 flex items-center gap-2 text-xs text-foreground-500">
              <i className="ri-chat-3-line"></i>
              {group.platform}
              <span className="h-3 w-px bg-background-300"></span>
              {group.category}
            </p>
          </div>
        </div>
        <span className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] ${trend.className}`}>
          <i className={trend.icon}></i>
          {trend.label}
        </span>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-3">
        <Stars rating={group.rating} />
        <span className="font-label text-sm font-medium text-foreground-800">{group.rating.toFixed(1)}</span>
        <span className="text-xs text-foreground-500">推荐指数</span>
      </div>

      <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-foreground-600">{group.summary}</p>

      <ul className="mt-3 flex flex-col gap-1.5">
        {group.highlights.map((item) => (
          <li key={item} className="flex items-start gap-2 text-xs leading-relaxed text-foreground-700">
            <span className="mt-1 flex h-3.5 w-3.5 shrink-0 items-center justify-center text-accent-600">
              <i className="ri-check-line"></i>
            </span>
            {item}
          </li>
        ))}
      </ul>

      <div className="mt-4 flex flex-wrap gap-1.5">
        {group.tags.map((tag) => (
          <span key={tag} className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-[11px] text-secondary-900">
            #{tag}
          </span>
        ))}
      </div>

      <div className="mt-auto pt-5">
        <div className="flex items-center justify-between text-xs text-foreground-600">
          <span className="inline-flex items-center gap-1.5">
            <i className="ri-group-line"></i>
            {group.members.toLocaleString("zh-CN")} 人
          </span>
          <span className="inline-flex items-center gap-1.5">
            活跃度
            <strong className="font-label font-semibold text-accent-700">{group.activity}</strong>
          </span>
        </div>
        <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-background-200">
          <span
            className="block h-full rounded-full bg-accent-500"
            style={{ width: `${group.activity}%` }}
          ></span>
        </div>
        <p className="mt-3 flex items-start gap-2 rounded-md bg-primary-50 px-3 py-2 text-xs leading-relaxed text-primary-800">
          <i className="ri-information-line mt-0.5"></i>
          {group.joinTip}
        </p>
      </div>
    </article>
  );
}